import useIndustryGetter from '@/pages/Market/hooks/useIndustryGetter';
import useReduxStateMarket, {
  MarketPageType,
} from '@/pages/Market/hooks/useReduxStateMarkets';
import PNV from '@components/PositiveNegativeValue';
import { memo, useEffect, useState } from 'react';
import { Bones } from '@simplywallst/ui-core';
import type { BreadcrumbItem } from '@components/Breadcrumb';
import Breadcrumb from '@components/Breadcrumb';
import {
  ROUTE_MARKET,
  ROUTE_MARKET_SECTOR,
  ROUTE_MARKET_SECTOR_SECONDARY,
} from '@/constants/routes';
import { format, parse } from 'date-fns';
import { ImageHeader } from '@/components/Header';
import {
  getIndustryCoverFromId,
  getMarketCoverFromCountryISO,
} from '@/utilities/coverImages';
import {
  getResponsiveImage,
  useCloudflareImage,
} from '@/hooks/useCloudflareImage';
import { useIndustryMetrics } from '@/hooks/useIndustryMetrics';
import useIndustryPath from '../../hooks/useIndustryPath';
import Filters from '../Filters';
import {
  Container,
  CrumbsWrapper,
  MarketBrief,
  MarketPerformance,
  MarketPerformanceValue,
  HeaderWrapper,
  MiscContainer,
  Navigation,
  Title,
  TitleContainer,
  TrendingIndustries,
} from './styled';
import useIndustryOptions from '@/pages/Market/hooks/useIndustryOptions';
import { Link } from '@/pages/Market/components/Filters/styled';
import useMarketSummary from '@/pages/Market/hooks/useMarketSummary';

const MemoFilters = memo(Filters);

interface Props {
  title: string;
}

export const Header = ({ title }: Props) => {
  const { market, marketPageType } = useReduxStateMarket();
  const [{ id }, enabled] = useIndustryGetter();
  const [crumbs, setCrumbs] = useState<readonly BreadcrumbItem[]>([]);
  const { path } = useIndustryPath(id, market);
  const { trendingIndustries = [] } = useIndustryOptions(true, market);
  const isMarketPage = marketPageType === MarketPageType.MARKET;

  const { summary, isFetching } = useMarketSummary(
    isMarketPage ? 'market-overview' : 'industry-overview'
  );

  const { averages: industryMetricsAverages, latestAnalysis } =
    useIndustryMetrics(market, id, !isMarketPage ? enabled : true);

  useEffect(() => {
    if (id) {
      setCrumbs([
        {
          name: market.toUpperCase() + ' Market',
          dispatch: {
            type: ROUTE_MARKET,
            payload: { market },
          },
        },
        ...path
          .filter(
            (industry) =>
              industry.level !== 'primary' &&
              industry.id.toString() !== id.toString()
          )
          .map((industry) => {
            const dispatch: ReducerAction = {
              type: ROUTE_MARKET_SECTOR,
              payload: {
                market,
                sector: industry.slug,
              },
            };

            if (industry.level === 'secondary') {
              dispatch.type = ROUTE_MARKET_SECTOR_SECONDARY;
              dispatch.payload.sector = path[0].slug;
              dispatch.payload.secondary = industry.slug;
            }

            return {
              name: industry.name,
              dispatch,
            };
          }),
      ]);
    } else {
      setCrumbs([]);
    }
  }, [market, id]);

  const backgroundImageUrl =
    marketPageType === MarketPageType.MARKET ||
    marketPageType === MarketPageType.HOME
      ? getMarketCoverFromCountryISO(market)
      : getIndustryCoverFromId(id, marketPageType);
  const { srcSet: coverImageSrcSet } = getResponsiveImage({
    src: backgroundImageUrl,
    breakpoints: [
      { width: 1024, height: 152 },
      { width: 1023, height: 135 },
      { width: 768, height: 135, fit: 'contain' },
      { width: 425, height: 135, fit: 'contain' },
      { width: 375, height: 135, fit: 'contain' },
    ],
  });

  const coverImageSrc = useCloudflareImage({
    src: backgroundImageUrl,
    width: 720,
    height: 152,
    fit: 'cover',
  });

  return (
    <Container>
      <Navigation>
        <MemoFilters />
      </Navigation>
      <HeaderWrapper>
        <TitleContainer>
          <ImageHeader
            imageSource={coverImageSrc}
            imageSourceSet={coverImageSrcSet}
          >
            {crumbs.length > 0 && (
              <CrumbsWrapper>
                <Breadcrumb list={crumbs} />
              </CrumbsWrapper>
            )}
            <Title data-cy-id="market-page-title">{title}</Title>
            <MiscContainer justifyContent="start" justifyItems="start">
              <div>
                <span>Updated</span>
                {typeof latestAnalysis?.analysisDate === 'string'
                  ? format(
                      parse(
                        latestAnalysis.analysisDate,
                        'yyyy-MM-dd',
                        new Date()
                      ),
                      'MMM dd, yyyy'
                    )
                  : '-'}
              </div>
              <div>
                <span>Data</span>
                Aggregated Company Financials
              </div>
              <div>
                <span>Companies</span>
                {latestAnalysis?.companyCount}
              </div>
            </MiscContainer>
          </ImageHeader>
        </TitleContainer>
        <MarketPerformance>
          <MarketPerformanceValue>
            <span>7D</span>
            <PNV
              data-cy-id="market-performance-7d"
              value={industryMetricsAverages.return7D}
            >
              {industryMetricsAverages.return7DFormatted}
            </PNV>
          </MarketPerformanceValue>
          <MarketPerformanceValue>
            <span>3M</span>
            <PNV
              data-cy-id="market-performance-3m"
              value={industryMetricsAverages.return90D}
            >
              {industryMetricsAverages.return90DFormatted}
            </PNV>
          </MarketPerformanceValue>
          <MarketPerformanceValue>
            <span>1Y</span>
            <PNV
              value={industryMetricsAverages.return1YrAbs}
              data-cy-id="market-performance-1y"
            >
              {industryMetricsAverages.return1YrAbsFormatted}
            </PNV>
          </MarketPerformanceValue>
          <MarketPerformanceValue>
            <span>YTD</span>
            <PNV
              value={industryMetricsAverages.returnYTD}
              data-cy-id="market-performance-ytd"
            >
              {industryMetricsAverages.returnYTDFormatted}
            </PNV>
          </MarketPerformanceValue>
        </MarketPerformance>
        {!isFetching ? (
          <MarketBrief data-cy-id="market-brief">{summary.text}</MarketBrief>
        ) : (
          // 5x Bones is roughly the height of MarketBrief
          <>
            <Bones maxWidth="100%" />
            <Bones maxWidth="100%" />
            <Bones maxWidth="100%" />
            <Bones maxWidth="100%" />
            <Bones maxWidth="100%" />
          </>
        )}
        {marketPageType === MarketPageType.MARKET &&
          trendingIndustries.length > 0 && (
            <TrendingIndustries
              alignItems="center"
              templateColumns="repeat(6, max-content)"
              columnGap="8px"
            >
              <span>Trending Industries</span>
              {trendingIndustries.map((a) => (
                <Link key={a.id} styleType="passive" to={a.href}>
                  {a.label}
                </Link>
              ))}
            </TrendingIndustries>
          )}
      </HeaderWrapper>
    </Container>
  );
};

export default Header;
